/**
 * src/pages/admin/AdminAnalytics.js
 * 
 * System Health & Analytics page.
 * metrics, charts, and health indicators.
 *  (no Cloud Functions, client-side Firestore only).
 */

import React, { useEffect, useState, useCallback } from "react";
import {
  Activity,
  Users,
  ShoppingBag,
  Star,
  TrendingUp,
  TrendingDown,
  BarChart3,
  RefreshCw,
  Globe,
  Package,
  Clock,
  Zap,
} from "lucide-react";
import * as adminService from "../../services/adminService";
import { errorDev } from '../../utils/logger.js';
import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
  CartesianGrid,
} from "recharts";

// ─── Metric Card ────────────────────────────────────────────────
function MetricCard({ title, value, subtitle, Icon, color }) {
  const colors = {
    green: { bg: "bg-green-100 dark:bg-green-900/30", icon: "text-green-600 dark:text-green-400" },
    blue: { bg: "bg-blue-100 dark:bg-blue-900/30", icon: "text-blue-600 dark:text-blue-400" },
    purple: { bg: "bg-purple-100 dark:bg-purple-900/30", icon: "text-purple-600 dark:text-purple-400" },
    amber: { bg: "bg-amber-100 dark:bg-amber-900/30", icon: "text-amber-600 dark:text-amber-400" },
    cyan: { bg: "bg-cyan-100 dark:bg-cyan-900/30", icon: "text-cyan-600 dark:text-cyan-400" },
    rose: { bg: "bg-rose-100 dark:bg-rose-900/30", icon: "text-rose-600 dark:text-rose-400" },
  };
  const c = colors[color] || colors.green;

  return (
    <div className="bg-white dark:bg-neutral-800 rounded-xl border border-gray-200 dark:border-neutral-700 p-4">
      <div className="flex items-center gap-3 mb-2">
        <div className={`w-10 h-10 rounded-lg ${c.bg} flex items-center justify-center`}>
          <Icon className={`w-5 h-5 ${c.icon}`} />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-xs text-gray-500 dark:text-gray-400">{title}</p>
          <p className="text-xl font-bold text-gray-900 dark:text-white">
            {value != null ? value.toLocaleString() : "—"}
          </p>
        </div>
      </div>
      {subtitle && (
        <p className="text-xs text-gray-500 dark:text-gray-400">{subtitle}</p>
      )}
    </div>
  );
}

// ─── Health Indicator ───────────────────────────────────────────
function HealthIndicator({ label, status, detail }) {
  const statusMap = {
    healthy: { color: "bg-green-500", text: "text-green-600 dark:text-green-400", label: "Healthy" },
    warning: { color: "bg-amber-500", text: "text-amber-600 dark:text-amber-400", label: "Warning" },
    critical: { color: "bg-red-500", text: "text-red-600 dark:text-red-400", label: "Critical" },
  };
  const s = statusMap[status] || statusMap.healthy;

  return (
    <div className="flex items-center justify-between py-2.5 border-b border-gray-100 dark:border-neutral-700 last:border-0">
      <div className="flex items-center gap-3">
        <div className={`w-2.5 h-2.5 rounded-full ${s.color}`} />
        <span className="text-sm text-gray-700 dark:text-gray-300">{label}</span>
      </div>
      <div className="text-right">
        <span className={`text-xs font-medium ${s.text}`}>{s.label}</span>
        {detail && (
          <p className="text-xs text-gray-400">{detail}</p>
        )}
      </div>
    </div>
  );
}

// ─── Main Page ──────────────────────────────────────────────────
export default function AdminAnalytics() {
  const [stats, setStats] = useState(null);
  const [signupData, setSignupData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const loadData = useCallback(async () => {
    try {
      const [statsData, signups] = await Promise.all([
        adminService.getPlatformStats(),
        adminService.getRecentSignups(30),
      ]);
      setStats(statsData);

      // Build daily chart
      const byDay = {};
      signups.forEach((u) => {
        const ts = u.createdAt?.seconds ? new Date(u.createdAt.seconds * 1000) : new Date(u.createdAt || 0);
        const day = ts.toISOString().slice(0, 10);
        byDay[day] = (byDay[day] || 0) + 1;
      });

      const chart = [];
      const today = new Date();
      for (let i = 29; i >= 0; i--) {
        const d = new Date(today);
        d.setDate(d.getDate() - i);
        const key = d.toISOString().slice(0, 10);
        chart.push({
          date: key,
          label: d.toLocaleDateString("en", { month: "short", day: "numeric" }),
          signups: byDay[key] || 0,
        });
      }
      setSignupData(chart);
    } catch (err) {
      // Silent fail
    errorDev("Operation failed:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleRefresh = async () => {
    setRefreshing(true);
    await loadData();
    setRefreshing(false);
  };

  // Derive health indicators
  const healthChecks = [
    {
      label: "Firestore Connection",
      status: stats ? "healthy" : "warning",
      detail: stats ? "Connected" : "Checking…",
    },
    {
      label: "User Registration",
      status: stats?.newUsersThisWeek > 0 ? "healthy" : "warning",
      detail: `${stats?.newUsersThisWeek || 0} new this week`,
    },
    {
      label: "Support Queue",
      status: (stats?.openTickets || 0) > 20 ? "critical" : (stats?.openTickets || 0) > 5 ? "warning" : "healthy",
      detail: `${stats?.openTickets || 0} open tickets`,
    },
    {
      label: "Content Pipeline",
      status: "healthy",
      detail: `${stats?.totalProducts || 0} products`,
    },
    {
      label: "Firebase Spark Plan",
      status: "healthy",
      detail: "No Cloud Functions required",
    },
  ];

  // Calculate total signups this month
  const totalSignupsThisMonth = signupData.reduce((sum, d) => sum + d.signups, 0);
  const avgDailySignups = signupData.length > 0 ? (totalSignupsThisMonth / signupData.length).toFixed(1) : 0;

  return (
    <div className="min-h-screen">
      {/* Header */}
      <div className="sticky top-0 z-20 bg-white dark:bg-neutral-900 border-b border-gray-200 dark:border-neutral-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="flex items-center justify-between h-14">
            <div className="flex items-center gap-3">
              <Activity className="w-5 h-5 text-green-600 dark:text-green-400" />
              <h1 className="text-lg font-bold text-gray-900 dark:text-white">
                System Health & Analytics
              </h1>
            </div>
            <button
              onClick={handleRefresh}
              disabled={refreshing}
              className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-green-700 dark:text-green-400 hover:bg-green-50 dark:hover:bg-green-900/20 rounded-lg transition disabled:opacity-50"
            >
              {refreshing ? (
                <span className="orrith-loader orrith-loader-xs orrith-loader-primary" />
              ) : (
                <RefreshCw className="w-4 h-4" />
              )}
              <span className="hidden sm:inline">Refresh</span>
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-5 pb-24 space-y-6">
        {/* Platform Metrics */}
        <section>
          <h2 className="text-sm font-bold text-gray-900 dark:text-white mb-3">
            Platform Metrics
          </h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            <MetricCard title="Total Users" value={stats?.totalUsers} Icon={Users} color="green" />
            <MetricCard title="Products" value={stats?.totalProducts} Icon={ShoppingBag} color="blue" />
            <MetricCard title="Offers" value={stats?.totalOffers} Icon={Package} color="cyan" />
            <MetricCard title="Ratings" value={stats?.totalRatings} Icon={Star} color="amber" />
            <MetricCard title="New This Week" value={stats?.newUsersThisWeek} Icon={TrendingUp} color="green" subtitle={`Avg ${avgDailySignups}/day`} />
          </div>
        </section>

        {/* Charts Row */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* User Growth Chart */}
          <div className="bg-white dark:bg-neutral-800 rounded-xl border border-gray-200 dark:border-neutral-700 p-4">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-sm font-bold text-gray-900 dark:text-white">User Growth</h3>
                <p className="text-xs text-gray-500 dark:text-gray-400">Daily new signups (30 days)</p>
              </div>
              <BarChart3 className="w-5 h-5 text-gray-400" />
            </div>
            <div className="h-56">
              {signupData.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={signupData} margin={{ top: 5, right: 5, left: 0, bottom: 0 }}>
                    <defs>
                      <linearGradient id="growth" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#16a34a" stopOpacity={0.3} />
                        <stop offset="95%" stopColor="#16a34a" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" vertical={false} />
                    <XAxis dataKey="label" tick={{ fontSize: 10, fill: "#9ca3af" }} tickLine={false} axisLine={false} interval="preserveEnd" />
                    <YAxis tick={{ fontSize: 10, fill: "#9ca3af" }} tickLine={false} axisLine={false} allowDecimals={false} />
                    <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8, border: "none", boxShadow: "0 4px 12px rgba(0,0,0,0.1)" }} />
                    <Area type="monotone" dataKey="signups" stroke="#16a34a" fill="url(#growth)" strokeWidth={2} dot={false} />
                  </AreaChart>
                </ResponsiveContainer>
              ) : (
                <div className="flex items-center justify-center h-full text-sm text-gray-400">
                  <div className="orrith-loader orrith-loader-sm orrith-loader-primary" aria-label="Loading user growth" />
                </div>
              )}
            </div>
          </div>

          {/* Daily Activity Bar Chart */}
          <div className="bg-white dark:bg-neutral-800 rounded-xl border border-gray-200 dark:border-neutral-700 p-4">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-sm font-bold text-gray-900 dark:text-white">Daily Registration</h3>
                <p className="text-xs text-gray-500 dark:text-gray-400">Bar view of signups</p>
              </div>
              <BarChart3 className="w-5 h-5 text-gray-400" />
            </div>
            <div className="h-56">
              {signupData.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={signupData.slice(-14)} margin={{ top: 5, right: 5, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" vertical={false} />
                    <XAxis dataKey="label" tick={{ fontSize: 10, fill: "#9ca3af" }} tickLine={false} axisLine={false} />
                    <YAxis tick={{ fontSize: 10, fill: "#9ca3af" }} tickLine={false} axisLine={false} allowDecimals={false} />
                    <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8, border: "none", boxShadow: "0 4px 12px rgba(0,0,0,0.1)" }} />
                    <Bar dataKey="signups" fill="#16a34a" radius={[4, 4, 0, 0]} maxBarSize={32} />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="flex items-center justify-center h-full text-sm text-gray-400">
                  <div className="orrith-loader orrith-loader-sm orrith-loader-primary" aria-label="Loading daily registration" />
                </div>
              )}
            </div>
          </div>
        </div>

        {/* System Health */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className="bg-white dark:bg-neutral-800 rounded-xl border border-gray-200 dark:border-neutral-700 p-4">
            <div className="flex items-center gap-2 mb-4">
              <Zap className="w-5 h-5 text-green-600 dark:text-green-400" />
              <h3 className="text-sm font-bold text-gray-900 dark:text-white">System Health</h3>
            </div>
            <div className="space-y-0">
              {healthChecks.map((check, i) => (
                <HealthIndicator key={i} {...check} />
              ))}
            </div>
          </div>

          <div className="bg-white dark:bg-neutral-800 rounded-xl border border-gray-200 dark:border-neutral-700 p-4">
            <div className="flex items-center gap-2 mb-4">
              <Globe className="w-5 h-5 text-blue-600 dark:text-blue-400" />
              <h3 className="text-sm font-bold text-gray-900 dark:text-white">Platform Summary</h3>
            </div>
            <div className="space-y-3">
              <div className="flex justify-between py-2 border-b border-gray-100 dark:border-neutral-700">
                <span className="text-sm text-gray-600 dark:text-gray-400">Firebase Plan</span>
                <span className="text-sm font-medium text-gray-900 dark:text-white">Spark (Free)</span>
              </div>
              <div className="flex justify-between py-2 border-b border-gray-100 dark:border-neutral-700">
                <span className="text-sm text-gray-600 dark:text-gray-400">Auth Method</span>
                <span className="text-sm font-medium text-gray-900 dark:text-white">Email + Google</span>
              </div>
              <div className="flex justify-between py-2 border-b border-gray-100 dark:border-neutral-700">
                <span className="text-sm text-gray-600 dark:text-gray-400">Database</span>
                <span className="text-sm font-medium text-gray-900 dark:text-white">Cloud Firestore</span>
              </div>
              <div className="flex justify-between py-2 border-b border-gray-100 dark:border-neutral-700">
                <span className="text-sm text-gray-600 dark:text-gray-400">Dark Mode</span>
                <span className="text-sm font-medium text-green-600 dark:text-green-400">Supported</span>
              </div>
              <div className="flex justify-between py-2">
                <span className="text-sm text-gray-600 dark:text-gray-400">Languages</span>
                <span className="text-sm font-medium text-gray-900 dark:text-white">EN / RW</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
